/* eslint-disable no-console */
import { DtoPage } from '@/types/cms';
import { datadogRum } from '@datadog/browser-rum';
import { Provider as ToolTipProvider } from '@radix-ui/react-tooltip';
import { HydrationBoundary } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initDatadogRum } from 'components/datadog-init';
import { ConfigContext } from 'context/Config';
import ReactQueryProvider from 'context/ReactQuery';
import { ApiClientProvider } from 'hooks/useApi';
import getErrorI18nConfig from 'i18n/errorConfig';
import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import type { AppProps as NextAppProps } from 'next/app';
import { useRouter } from 'next/router';
import NextNProgress from 'nextjs-progressbar';
import { useEffect } from 'react';
import { env } from 'next-runtime-env';
import { sessionRefetchIntervalInSeconds } from 'utils/auth';
import CookieConsent from 'utils/CookieConsent';
import { sans, tiller } from 'utils/fonts';
import { setLocale } from 'yup';
import PlausibleProvider from 'next-plausible';

// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import nextI18NextConfig from '../../next-i18next.config';
import ModalProvider from '../context/Modal/ModalContext';
import { ToasterProvider } from '../context/Toaster/ToasterContext';
import { useDateFnsWithLocale } from '../hooks/i18n';
import '../styles/globals.css';

type AppProps = NextAppProps<{
  session: Session;
  generalSettings: DtoPage['generalSettings'];
  isMobileApp: boolean;
  preferredUserLocale: string;
  dehydratedState: unknown;
}>;

initDatadogRum();

const sha = env('NEXT_PUBLIC_COMMIT_SHORT_SHA');
console.log('🚀', sha);

const App = ({
  Component,
  pageProps: { session, generalSettings, isMobileApp, preferredUserLocale, ...pageProps },
}: AppProps) => {
  const router = useRouter();
  const is404 = !generalSettings;
  const { format } = useDateFnsWithLocale();
  setLocale(getErrorI18nConfig(format));

  useEffect(() => {
    if (session?.userId) {
      datadogRum.setUser({
        id: session.userId,
      });
    }
  }, [session?.userId]);

  // // Log build version info in preprod for debugging
  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     const boEnv = env('NEXT_PUBLIC_BO_ENV');
  //     const buildVersion = env('NEXT_PUBLIC_BUILD_VERSION');
  //     if (boEnv === 'preprod' && buildVersion) {
  //       console.log('🚀 Deployed PR/Build:', buildVersion);
  //     } else if (buildVersion) {
  //       console.log('🏠 Local Build Version:', buildVersion);
  //     }
  //   }
  // }, []);

  return (
    <>
      <style global jsx>
        {`
          :root {
            --font-sans: ${sans.style.fontFamily};
            --font-tiller: ${tiller.style.fontFamily};
          }
        `}
      </style>
      <NextNProgress color="#BD8049" />
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
      <ConfigContext.Provider value={{ isMobileApp, preferredUserLocale }}>
        <PlausibleProvider
          customDomain={typeof window !== 'undefined' ? window.location.origin : ''}
          domain={typeof window !== 'undefined' ? window.location.host : ''}
          pageviewProps={{
            mobileApp: isMobileApp ? 'yes' : 'no',
            loggedIn: session?.userId ? 'yes' : 'no',
            userId: session?.userId,
          }}
          scriptProps={{
            src: 'js/script.js',
          }}
          enabled
        >
          <SessionProvider
            key={session?.accessToken}
            refetchInterval={sessionRefetchIntervalInSeconds}
            session={session}
          >
            <ApiClientProvider>
              <ToolTipProvider>
                <ToasterProvider>
                  <ReactQueryProvider>
                    <HydrationBoundary state={pageProps.dehydratedState}>
                      <ReactQueryDevtools />
                      <ModalProvider key={router.asPath}>
                        <Component {...pageProps} />
                      </ModalProvider>
                      {!is404 && <CookieConsent />}
                    </HydrationBoundary>
                  </ReactQueryProvider>
                </ToasterProvider>
              </ToolTipProvider>
            </ApiClientProvider>
          </SessionProvider>
        </PlausibleProvider>
      </ConfigContext.Provider>
    </>
  );
};

const config: any = nextI18NextConfig;
export default appWithTranslation(App, config);
